import { all, one } from '../lib/dom';

/**
 * Live water levels and river flows for the St. Louis River basin.
 *
 * Ported from the standalone React page at 218waterlevels.com. React, ReactDOM
 * and the 60 KB design-tool runtime it loaded from a CDN are gone; the render is
 * two tables and one inline SVG, which is a handful of DOM calls. The arithmetic
 * — chart scaling, target deviation, day-collapsing — is a faithful port.
 *
 * Three sources, all read-only:
 *
 *   1. An Apps Script `doGet` returning the current reading for every displayed
 *      tag. This is the only source that is live to the minute.
 *   2. Daily elevation history — one row per day. Drives the elevation chart.
 *   3. Flow history — every tag, several readings per day. Drives the flow
 *      chart. Despite looking like a raw archive, it is NOT spare capacity:
 *      the flow series has no other source.
 *
 * The history sources are fetched from the Apps Script first (`?view=daily` /
 * `?view=flow`), which works even when the sheet is private, and fall back to
 * the sheet's public gviz CSV export for as long as a pre-view deployment of
 * the script is live. Once the script deployment carries the views, the sheet
 * can stop being shared publicly (TODO task 9).
 *
 * The current reading is overlaid onto today's point in both series, so the last
 * point on the chart always matches the table above it.
 *
 * No-ops on every page that lacks `[data-water-levels]`.
 */

const SHEET_ID = '1QA9Qb4vl3swPUI9ZGWnvg1h4hPqbSfSyLj2rJdDBogA';
const WEBAPP_URL =
  'https://script.google.com/macros/s/AKfycbzEEKt014OXSxrBjU3jmMt8ZuWjjUmTtBv2lgPUiOqVCIAHpSEr9ATXcGHywJVjtBn4/exec';

/** Summer target pool elevation, feet. Fixed by Minnesota Power, not derived. */
const RESERVOIRS = [
  { key: 'island', name: 'Island Lake', target: 1368.81 },
  { key: 'fish', name: 'Fish Lake', target: 1351.72 },
  { key: 'whiteface', name: 'Whiteface', target: 1452.92 },
] as const;

type ResKey = (typeof RESERVOIRS)[number]['key'];

/** Raw feed tags for the flow columns in the Full Log tab. */
const FLOW_TAG: Record<ResKey, string> = {
  island: 'ISLAND_FLOW_TOTAL',
  fish: 'FISH_FLOW_TOTAL',
  whiteface: 'WF_FLOW_TOTAL',
};

const RANGE_DAYS = [
  { key: '5d', label: '5 Days', days: 5 },
  { key: '1w', label: 'Week', days: 7 },
  { key: '1m', label: 'Month', days: 30 },
  { key: '3m', label: '3 Months', days: 90 },
  { key: '6m', label: '6 Months', days: 180 },
  { key: '1y', label: 'Year', days: 365 },
] as const;

/* Theme palette — matches dd-content/theme/assets/styles/theme.min.css. */
const C = {
  primary: '#0078b1',
  dark: '#004465',
  teal: '#00bec3',
  cream: '#ffdd96',
  above: '#0e8a6b',
  below: '#cf5a2e',
  muted: '#888',
} as const;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type Live = Partial<Record<string, number>>;
type Point = { ts: string; val: number };
type DailyRow = { date: string } & Partial<Record<ResKey, number>>;
type Status = 'loading' | 'ok' | 'error';

interface State {
  live: Live;
  daily: DailyRow[];
  flow: Partial<Record<ResKey, Point[]>>;
  selected: ResKey;
  metric: 'elevation' | 'flow';
  range: (typeof RANGE_DAYS)[number]['key'];
  dailyStatus: Status;
  flowStatus: Status;
  lastSync: string;
}

/* ---------------------------------------------------------------- utilities */

/**
 * Calendar date in the feed's timezone as YYYY-MM-DD. The sheet is stamped in
 * Central time, so "today" must be Central's today — not the visitor's and not
 * UTC's. A visitor in Europe is up to 8 hours ahead; using their local date
 * would overlay the live reading onto a date the sheet doesn't have yet.
 */
function centralToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

function toISO(raw: string): string {
  const s = (raw || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (m && m[1] && m[2] && m[3]) {
    const y = m[3].length === 2 ? `20${m[3]}` : m[3];
    return `${y}-${String(+m[1]).padStart(2, '0')}-${String(+m[2]).padStart(2, '0')}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  // Local date parts, not toISOString(): that converts through UTC and can
  // shift the calendar date for evening timestamps.
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(iso: string): string {
  const p = (iso || '').slice(0, 10).split('-');
  if (p.length !== 3 || !p[1] || !p[2]) return iso;
  return `${MONTHS[parseInt(p[1], 10) - 1] ?? ''} ${parseInt(p[2], 10)}`;
}

/** One CSV line to cells, honouring quoted fields and "" escapes. */
function cells(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else quoted = false;
      } else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      out.push(cur);
      cur = '';
    } else cur += c;
  }
  out.push(cur);
  return out;
}

function csvRows(text: string): string[][] {
  return text
    .replace(/\r/g, '')
    .split('\n')
    .filter((l) => l.trim().length)
    .map(cells);
}

/** Google returns an HTML login page rather than an error when a sheet is private. */
function looksLikeHtml(text: string): boolean {
  return /<html|<!doctype/i.test(text.slice(0, 200));
}

function gvizUrl(sheet?: string): string {
  const base = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv`;
  const tab = sheet ? `&sheet=${encodeURIComponent(sheet)}` : '';
  return `${base}${tab}&_=${Date.now()}`;
}

/* ------------------------------------------------------------------ fetches */

async function fetchLive(): Promise<Live> {
  const res = await fetch(WEBAPP_URL);
  if (!res.ok) throw new Error('live http');
  return (await res.json()) as Live;
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`http ${res.status}`);
  return res.json() as Promise<unknown>;
}

/**
 * Preferred path: history straight from the Apps Script. An older script
 * deployment ignores the `view` parameter and returns the live object instead
 * — the shape checks reject that and the CSV fallback takes over, so this is
 * safe to ship before the script is redeployed.
 */
async function fetchDaily(): Promise<DailyRow[]> {
  try {
    const rows = parseDailyJson(await fetchJson(`${WEBAPP_URL}?view=daily`));
    if (rows.length) return rows;
  } catch {
    // fall through to the CSV export
  }
  return fetchDailyCsv();
}

function parseDailyJson(data: unknown): DailyRow[] {
  if (!Array.isArray(data)) return [];
  const rows: DailyRow[] = [];
  for (const raw of data) {
    if (typeof raw !== 'object' || raw === null) continue;
    const o = raw as Record<string, unknown>;
    const date = o['date'];
    if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    const row: DailyRow = { date };
    (Object.keys(FLOW_TAG) as ResKey[]).forEach((k) => {
      const v = o[k];
      if (typeof v === 'number' && !isNaN(v)) row[k] = v;
    });
    rows.push(row);
  }
  rows.sort((a, b) => a.date.localeCompare(b.date));
  return rows;
}

async function fetchDailyCsv(): Promise<DailyRow[]> {
  const res = await fetch(gvizUrl());
  if (!res.ok) throw new Error('daily http');
  const text = await res.text();
  if (looksLikeHtml(text)) throw new Error('not public');

  const data = csvRows(text);
  const first = data[0];
  // The export may or may not carry a header row depending on sheet state.
  const start = first && first.some((s) => /date|island|fish|white|elev/i.test(s)) ? 1 : 0;

  const rows: DailyRow[] = [];
  for (let i = start; i < data.length; i++) {
    const c = data[i];
    if (!c) continue;
    const date = (c[0] ?? '').trim();
    if (!date) continue;
    rows.push({
      date: toISO(date),
      island: parseFloat(c[1] ?? ''),
      fish: parseFloat(c[2] ?? ''),
      whiteface: parseFloat(c[3] ?? ''),
    });
  }
  rows.sort((a, b) => a.date.localeCompare(b.date));
  return rows;
}

async function fetchFlow(): Promise<Partial<Record<ResKey, Point[]>>> {
  try {
    const series = parseFlowJson(await fetchJson(`${WEBAPP_URL}?view=flow`));
    if (series) return series;
  } catch {
    // fall through to the CSV export
  }
  return fetchFlowCsv();
}

function parseFlowJson(data: unknown): Partial<Record<ResKey, Point[]>> | null {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  const series: Partial<Record<ResKey, Point[]>> = {};
  let total = 0;
  for (const k of Object.keys(FLOW_TAG) as ResKey[]) {
    const arr = o[k];
    if (!Array.isArray(arr)) return null;
    const points: Point[] = [];
    for (const raw of arr) {
      if (typeof raw !== 'object' || raw === null) continue;
      const p = raw as Record<string, unknown>;
      if (typeof p['ts'] === 'string' && typeof p['val'] === 'number' && !isNaN(p['val'])) {
        points.push({ ts: p['ts'], val: p['val'] });
      }
    }
    series[k] = points;
    total += points.length;
  }
  return total > 0 ? series : null;
}

async function fetchFlowCsv(): Promise<Partial<Record<ResKey, Point[]>>> {
  // The tab name embeds the year in Central time — the sheet's clock, which is
  // not the visitor's around New Year.
  const res = await fetch(gvizUrl(`Full Log ${centralToday().slice(0, 4)}`));
  if (!res.ok) throw new Error('flow http');
  const text = await res.text();
  if (looksLikeHtml(text)) throw new Error('not public');

  const rows = csvRows(text);

  // The gviz export collapses the sheet's two header rows (friendly label, then
  // raw tag) into one cell like "Alden Lake — Flow (cfs) ALDEN_FLOW", so match
  // on suffix rather than equality — and still handle a tag-only header.
  const colFor = (header: string[], tag: string): number =>
    header.findIndex((c) => {
      const t = (c ?? '').trim();
      return t === tag || t.endsWith(` ${tag}`) || t.endsWith(tag);
    });

  let tagRow = -1;
  for (let i = 0; i < Math.min(rows.length, 3); i++) {
    const r = rows[i];
    if (r && colFor(r, 'ISLAND_FLOW_TOTAL') >= 0) {
      tagRow = i;
      break;
    }
  }
  if (tagRow < 0) return {};

  const header = rows[tagRow] ?? [];
  const idx = {} as Record<ResKey, number>;
  (Object.keys(FLOW_TAG) as ResKey[]).forEach((k) => {
    idx[k] = colFor(header, FLOW_TAG[k]);
  });

  const series: Partial<Record<ResKey, Point[]>> = {};
  (Object.keys(FLOW_TAG) as ResKey[]).forEach((k) => (series[k] = []));

  for (let i = tagRow + 1; i < rows.length; i++) {
    const c = rows[i];
    if (!c) continue;
    const ts = (c[0] ?? '').trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(ts)) continue;
    (Object.keys(FLOW_TAG) as ResKey[]).forEach((k) => {
      const col = idx[k];
      if (col < 0) return;
      const v = parseFloat(c[col] ?? '');
      if (!isNaN(v)) series[k]?.push({ ts, val: v });
    });
  }
  return series;
}

/* --------------------------------------------------------------- transforms */

/**
 * The Full Log holds several intraday readings; collapse to the last reading of
 * each calendar day so the flow trend reads day-over-day like the elevation
 * chart, whose source is already daily.
 */
function collapseDaily(points: Point[]): Point[] {
  const byDay = new Map<string, number>();
  [...points]
    .sort((a, b) => a.ts.localeCompare(b.ts))
    .forEach((p) => {
      const day = p.ts.slice(0, 10);
      if (day) byDay.set(day, p.val);
    });
  return [...byDay.keys()].sort().map((ts) => ({ ts, val: byDay.get(ts) as number }));
}

/** Series for the selected lake and metric, with today's live value overlaid. */
function seriesFor(s: State): Point[] {
  let points: Point[];
  let liveVal: number | undefined;

  if (s.metric === 'flow') {
    points = collapseDaily(s.flow[s.selected] ?? []);
    liveVal = s.live[`${s.selected}_flow`];
  } else {
    points = s.daily
      .filter((r) => typeof r[s.selected] === 'number' && !isNaN(r[s.selected] as number))
      .map((r) => ({ ts: r.date, val: r[s.selected] as number }));
    liveVal = s.live[`${s.selected}_elev`];
  }

  if (typeof liveVal === 'number' && !isNaN(liveVal)) {
    const today = centralToday();
    const existing = points.find((p) => p.ts === today);
    if (existing) existing.val = liveVal;
    else points.push({ ts: today, val: liveVal });
  }
  return points;
}

/** Deviation from summer target, phrased in inches. */
function deviation(elev: number, target: number): { text: string; above: boolean } {
  const inches = (elev - target) * 12;
  const r = Math.round(Math.abs(inches) * 100) / 100;
  if (r === 0) return { text: 'At target', above: true };
  return { text: `${r.toFixed(2)} in ${inches > 0 ? 'above' : 'below'}`, above: inches > 0 };
}

/* ------------------------------------------------------------------- render */

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

function renderConditions(root: HTMLElement, s: State): void {
  const body = one('[data-wl-conditions]', root);
  if (!body) return;

  const fmtFlow = (v: number | undefined): string =>
    typeof v !== 'number' || isNaN(v) ? '—' : Math.round(v).toLocaleString();

  body.innerHTML = RESERVOIRS.map((r) => {
    const elev = s.live[`${r.key}_elev`];
    const flow = s.live[`${r.key}_flow`];
    const known = typeof elev === 'number' && !isNaN(elev);
    const dev = known ? deviation(elev, r.target) : null;
    const within = known && Math.abs(elev - r.target) <= 0.25;
    return `<tr>
      <th scope="row" class="wl-name">${esc(r.name)}</th>
      <td class="wl-num wl-strong">${known ? elev.toFixed(2) : '—'}</td>
      <td class="wl-num wl-dim">${r.target.toFixed(2)}</td>
      <td class="wl-num"><span class="wl-pill${within ? ' is-within' : ''}">${dev ? esc(dev.text) : '—'}</span></td>
      <td class="wl-num wl-strong wl-flow">${fmtFlow(flow)}</td>
    </tr>`;
  }).join('');
}

function renderChart(root: HTMLElement, s: State): void {
  const host = one('[data-wl-chart]', root);
  const status = one('[data-wl-status]', root);
  if (!host || !status) return;

  const isFlow = s.metric === 'flow';
  // Window the chart to the selected range. Without this the chart drew the
  // entire Daily history, which grows one row per day forever — at a year of
  // data that is 365 overlapping 5 px dots on a 710 px axis.
  const days = RANGE_DAYS.find((r) => r.key === s.range)?.days ?? 5;
  const readings = seriesFor(s).slice(-days);
  const activeStatus = isFlow ? s.flowStatus : s.dailyStatus;

  if (!readings.length) {
    host.innerHTML = '';
    host.hidden = true;
    status.hidden = false;
    const lake = RESERVOIRS.find((r) => r.key === s.selected)?.name ?? '';
    status.textContent =
      activeStatus === 'loading'
        ? 'Loading readings…'
        : activeStatus === 'error'
          ? 'Could not read the data source. It may be offline or no longer shared publicly.'
          : `No ${lake} ${isFlow ? 'flow' : 'elevation'} readings logged yet.`;
    return;
  }
  status.hidden = true;
  host.hidden = false;

  const target = isFlow ? null : (RESERVOIRS.find((r) => r.key === s.selected)?.target ?? null);
  const vals = readings.map((r) => r.val);

  // Y range: flow floors at zero and pads proportionally; elevation snaps to
  // quarter-feet and always keeps the target line in frame.
  let yMin: number;
  let yMax: number;
  if (isFlow || target === null) {
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = Math.max((hi - lo) * 0.18, hi * 0.06, 0.5);
    yMin = Math.max(0, lo - pad);
    yMax = hi + pad;
    if (yMax - yMin < 1) yMax = yMin + 1;
  } else {
    const lo = Math.min(target, ...vals);
    const hi = Math.max(target, ...vals);
    yMin = Math.floor((lo - 0.25) * 4) / 4;
    yMax = Math.ceil((hi + 0.25) * 4) / 4;
    if (yMax - yMin < 0.5) {
      const mid = (yMin + yMax) / 2;
      yMin = mid - 0.3;
      yMax = mid + 0.3;
    }
  }

  const L = 72;
  const R = 782;
  const T = 46;
  const B = 286;
  const n = readings.length;
  const sx = (i: number): number => (n > 1 ? L + (R - L) * (i / (n - 1)) : (L + R) / 2);
  const sy = (v: number): number => T + (1 - (v - yMin) / (yMax - yMin)) * (B - T);

  const pts = readings.map((r, i) => ({ x: sx(i), y: sy(r.val) }));
  const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = n > 1 ? `${line} ${R},${B} ${L},${B}` : '';

  const grid = Array.from({ length: 5 }, (_, k) => {
    const v = yMin + (yMax - yMin) * (k / 4);
    const y = sy(v);
    return { y, label: isFlow ? Math.round(v).toLocaleString() : v.toFixed(2) };
  });

  const step = n <= 9 ? 1 : Math.ceil(n / 9);
  const edgeAnchor = (i: number): string => (n === 1 ? 'middle' : i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle');

  // Walk back from the newest point rather than forward from the oldest. The
  // last label is always drawn, so stepping forward can leave it a fraction of
  // a step from its neighbour and the two overlap ("Jul 26Jul 28"). Anchoring
  // on the end keeps every gap exactly `step` wide.
  const labelIdx = new Set<number>();
  for (let i = n - 1; i >= 0; i -= step) labelIdx.add(i);

  const gridSvg = grid
    .map(
      (g) =>
        `<line x1="${L}" x2="${R}" y1="${g.y.toFixed(1)}" y2="${g.y.toFixed(1)}" stroke="#eee" stroke-width="1"/>` +
        `<text x="${L - 10}" y="${(g.y + 4).toFixed(1)}" text-anchor="end" font-size="12" fill="#8a8f93">${g.label}</text>`,
    )
    .join('');

  const targetSvg =
    target === null
      ? ''
      : `<line x1="${L}" x2="${R}" y1="${sy(target).toFixed(1)}" y2="${sy(target).toFixed(1)}" stroke="${C.cream}" stroke-width="2.5" stroke-dasharray="7 5"/>`;

  const dotsSvg = pts
    .map((p) => `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="5" fill="${C.primary}" stroke="#fff" stroke-width="2"/>`)
    .join('');

  // Label every point when the series is short; otherwise only the newest, so
  // labels never collide.
  const labelsSvg = readings
    .map((r, i) => {
      if (n > 10 && i !== n - 1) return '';
      const dev = target === null ? null : deviation(r.val, target);
      const above = dev ? dev.above : true;
      const text = isFlow ? `${r.val >= 100 ? Math.round(r.val).toLocaleString() : r.val.toFixed(1)} cfs` : (dev?.text ?? '');
      const fill = isFlow ? C.primary : above ? C.above : C.below;
      const y = sy(r.val) + (above ? -14 : 22);
      return `<text x="${sx(i).toFixed(1)}" y="${y.toFixed(1)}" text-anchor="${edgeAnchor(i)}" font-size="11" font-weight="700" fill="${fill}">${esc(text)}</text>`;
    })
    .join('');

  const xLabelsSvg = readings
    .map((r, i) =>
      labelIdx.has(i)
        ? `<text x="${sx(i).toFixed(1)}" y="308" text-anchor="${edgeAnchor(i)}" font-size="12" fill="${C.muted}">${esc(fmtDate(r.ts))}</text>`
        : '',
    )
    .join('');

  host.innerHTML =
    `<svg viewBox="0 0 800 340" role="img" aria-label="${esc(
      `${RESERVOIRS.find((r) => r.key === s.selected)?.name ?? ''} ${isFlow ? 'flow' : 'elevation'} over time`,
    )}">` +
    `<text x="8" y="22" font-size="12" font-weight="700" fill="${C.muted}">${isFlow ? 'Flow (cubic ft/sec)' : 'Elevation (feet)'}</text>` +
    gridSvg +
    (area ? `<polygon points="${area}" fill="rgba(0,120,177,0.10)"/>` : '') +
    targetSvg +
    (n > 1 ? `<polyline points="${line}" fill="none" stroke="${C.primary}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>` : '') +
    dotsSvg +
    labelsSvg +
    xLabelsSvg +
    `<text x="425" y="332" text-anchor="middle" font-size="13" font-weight="700" fill="${C.muted}">Date</text>` +
    `</svg>`;

  renderReadings(root, s, readings, target);
}

function renderReadings(root: HTMLElement, s: State, readings: Point[], target: number | null): void {
  const body = one('[data-wl-readings]', root);
  const head = one('[data-wl-readings-head]', root);
  if (!body || !head) return;

  const isFlow = s.metric === 'flow';
  const days = RANGE_DAYS.find((r) => r.key === s.range)?.days ?? 5;
  const windowed = readings.slice(Math.max(0, readings.length - days)).reverse();

  head.innerHTML =
    `<th scope="col">Date</th><th scope="col" class="wl-num">${isFlow ? 'Flow (cfs)' : 'Elevation (ft)'}</th>` +
    (target === null ? '' : '<th scope="col" class="wl-num">vs. Target</th>');

  body.innerHTML = windowed
    .map((r) => {
      const dev = target === null ? null : deviation(r.val, target);
      const val = isFlow ? (r.val >= 100 ? Math.round(r.val).toLocaleString() : r.val.toFixed(1)) : r.val.toFixed(2);
      return `<tr><th scope="row" class="wl-name">${esc(fmtDate(r.ts))}</th><td class="wl-num wl-strong">${val}</td>${
        dev ? `<td class="wl-num" style="color:${dev.above ? C.above : C.below}">${esc(dev.text)}</td>` : ''
      }</tr>`;
    })
    .join('');
}

function renderControls(root: HTMLElement, s: State): void {
  all('[data-wl-lake]', root).forEach((b) => {
    b.classList.toggle('is-active', b.dataset['wlLake'] === s.selected);
    b.setAttribute('aria-pressed', String(b.dataset['wlLake'] === s.selected));
  });
  all('[data-wl-range]', root).forEach((b) => {
    b.classList.toggle('is-active', b.dataset['wlRange'] === s.range);
    b.setAttribute('aria-pressed', String(b.dataset['wlRange'] === s.range));
  });
  const metric = one<HTMLSelectElement>('[data-wl-metric]', root);
  if (metric) metric.value = s.metric;

  const title = one('[data-wl-title]', root);
  if (title) {
    const lake = RESERVOIRS.find((r) => r.key === s.selected)?.name ?? '';
    title.textContent = `${lake} ${s.metric === 'flow' ? 'Flow Output' : 'Elevation'}`;
  }
  const legend = one('[data-wl-target-legend]', root);
  if (legend) legend.hidden = s.metric === 'flow';

  const sync = one('[data-wl-sync]', root);
  if (sync) {
    const st = s.metric === 'flow' ? s.flowStatus : s.dailyStatus;
    sync.textContent =
      st === 'loading' ? 'Syncing…' : st === 'error' ? 'Not connected' : s.lastSync ? `Synced ${s.lastSync}` : '';
  }
}

/* ---------------------------------------------------------------- component */

export function waterLevels(): void {
  const root = one('[data-water-levels]');
  if (!root) return;

  const state: State = {
    live: {},
    daily: [],
    flow: {},
    selected: 'island',
    metric: 'elevation',
    range: '5d',
    dailyStatus: 'loading',
    flowStatus: 'loading',
    lastSync: '',
  };

  const draw = (): void => {
    renderControls(root, state);
    renderConditions(root, state);
    renderChart(root, state);
  };

  const load = (): void => {
    state.dailyStatus = 'loading';
    state.flowStatus = 'loading';
    draw();

    // Independent on purpose: a failure in any one source still lets the others
    // render, rather than blanking the whole page.
    void fetchLive()
      .then((live) => {
        state.live = { ...state.live, ...live };
      })
      .catch(() => undefined)
      .finally(draw);

    void fetchDaily()
      .then((rows) => {
        state.daily = rows;
        state.dailyStatus = 'ok';
        state.lastSync = new Date().toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
      })
      .catch(() => {
        state.dailyStatus = 'error';
      })
      .finally(draw);

    void fetchFlow()
      .then((series) => {
        state.flow = series;
        state.flowStatus = 'ok';
      })
      .catch(() => {
        state.flowStatus = 'error';
      })
      .finally(draw);
  };

  root.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target.closest<HTMLElement>('button') : null;
    if (!el) return;

    const lake = el.dataset['wlLake'];
    const range = el.dataset['wlRange'];
    if (lake && RESERVOIRS.some((r) => r.key === lake)) {
      state.selected = lake as ResKey;
      draw();
    } else if (range && RANGE_DAYS.some((r) => r.key === range)) {
      state.range = range as State['range'];
      draw();
    } else if (el.hasAttribute('data-wl-refresh')) {
      load();
    }
  });

  root.addEventListener('change', (event) => {
    const el = event.target;
    if (el instanceof HTMLSelectElement && el.hasAttribute('data-wl-metric')) {
      state.metric = el.value === 'flow' ? 'flow' : 'elevation';
      draw();
    }
  });

  load();
}
